'use client';

import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { branding } from '@/config/branding';

const chapters = [
  {
    eyebrow: 'Wallet preparation',
    title: <>Prepare<br />every wallet.</>,
    description: 'Source wallets, monitor balances and fund them from one place.',
    points: ['CEX Funding', 'Wallet Marketplace', 'Live Monitoring', 'Bulk Funding'],
    image: branding.storyArtwork.wallets,
    imageAlt: 'Wallet marketplace and Solana wallet funding preview',
  },
  {
    eyebrow: 'Launch setup',
    title: <>Choose where<br />and how<br />to launch.</>,
    description: 'Select a Solana launchpad. Add token details. Set supply and launch controls.',
    points: ['Sniper Guard', 'Block 0', 'Token Metadata', 'Supply Acquisition'],
    image: null,
    imageAlt: 'Solana token launch configuration preview',
  },
  {
    eyebrow: 'Post-launch',
    title: <>Monitor your<br />positions.<br />Automate<br />your trades.</>,
    description: 'Track who’s trading your token, manage your supply and positions, and automate your strategy. All in one place.',
    points: [],
    image: branding.storyArtwork.trading,
    imageAlt: 'Solana chart and live trade feed preview',
  },
];

function LaunchStage() {
  return (
    <div className="story-launch-stage" aria-label="Solana launch setup preview">
      <div className="story-launch-top"><span>Launch setup</span><span>Token details</span><span>Launch settings</span><span>Review</span></div>
      <div className="story-launch-body">
        <div className="story-launch-route"><span className="story-solana-mark">◎</span><span><b>Solana</b><small>Network</small></span><span className="story-route-divider" /><span className="story-pump-mark">P</span><span><b>Pump.fun</b><small>Launchpad</small></span><span className="story-route-check">✓</span></div>
        <div className="story-launch-heading"><span className="story-token-mark">C</span><span><b>Cabal</b><small>$CABAL · Solana</small></span><i>Draft</i></div>
        <div className="story-launch-fields"><label>Project name<span>Cabal</span></label><label>Token symbol<span>$CABAL</span></label><label className="story-wide">Description<span>Token launch and market tools in one platform.</span></label><label className="story-wide">Total supply<span>1,000,000,000</span></label></div>
        <div className="story-blockzero"><span><b>Block-0</b><small>Acquire supply at launch</small></span><i><em /></i></div>
        <div className="story-launch-wallets"><span>Selected wallets</span><b>6 wallets</b><strong>30% supply to acquire</strong></div>
        <div className="story-launch-button">Continue&nbsp; →</div>
      </div>
    </div>
  );
}

export default function FeatureStory() {
  const [activeChapter, setActiveChapter] = useState(0);
  const chapterRefs = useRef<Array<HTMLElement | null>>([]);

  useEffect(() => {
    const elements = chapterRefs.current.filter((element): element is HTMLElement => element !== null);
    if (!('IntersectionObserver' in window) || elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const current = entries.find((entry) => entry.isIntersecting);
        if (!current) return;
        const index = Number((current.target as HTMLElement).dataset.chapterIndex);
        if (Number.isInteger(index)) setActiveChapter(index);
      },
      { rootMargin: '-43% 0px -43% 0px', threshold: 0 },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="feature-story" id="platform" aria-label="Platform overview">
      <div className="feature-chapters">
        {chapters.map((chapter, index) => (
          <article
            className={`feature-chapter${activeChapter === index ? ' is-active' : ''}`}
            data-chapter-index={index}
            key={chapter.eyebrow}
            ref={(element) => { chapterRefs.current[index] = element; }}
          >
            <div className={`feature-copy feature-copy-${index}`}>
              <span className="eyebrow">{chapter.eyebrow}</span>
              <h2>{chapter.title}</h2>
              <p>{chapter.description}</p>
              {chapter.points.length > 0 && (
                <ul>{chapter.points.map((point, pointIndex) => <li key={point}><i>{['↗', '◈', '⌁', '↗'][pointIndex]}</i>{point}{point === 'Wallet Marketplace' && <b className="new">NEW</b>}</li>)}</ul>
              )}
              {index === 2 && <div className="feature-columns"><div><b>Monitor</b><span>◈&nbsp; Live Chart</span><span>⌁&nbsp; Trade Feed</span><span>◎&nbsp; KOL Tracker</span></div><div><b>Operate</b><span>◒&nbsp; Buy &amp; Sell</span><span>♧&nbsp; Supply Control</span><span>↗&nbsp; Claim Rewards</span></div><div><b>Automate</b><span>◷&nbsp; AutoSell</span><span>◉&nbsp; AutoBuy</span><span>▥&nbsp; Volume Bot</span></div></div>}
            </div>
            <div className="feature-mobile-preview" aria-hidden="true">
              {chapter.image ? <Image src={chapter.image} width={455} height={506} alt="" /> : <LaunchStage />}
            </div>
          </article>
        ))}
      </div>
      <div className="feature-stage" aria-hidden="true">
        <div className="feature-stage-sticky">
          <div className="feature-stage-frame">
            {chapters.map((chapter, index) => (
              <div className={`feature-stage-scene${activeChapter === index ? ' is-active' : ''}`} key={chapter.eyebrow}>
                {chapter.image ? <Image src={chapter.image} width={455} height={506} alt={chapter.imageAlt} /> : <LaunchStage />}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
